/**
 * Lawity Dashboard Demo Page
 * Showcases the Morphing Dashboard with context-aware layouts
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  Moon,
  Sun,
  Activity,
  MapPin,
  Briefcase,
  Keyboard,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { MorphingDashboard } from '../components/generative/MorphingDashboard';
import { LawityProvider, useLawityContext } from '../context/LawityContext';
import { CommandPalette } from '../components/common/CommandPalette';
import { KeyboardShortcutsHelp } from '../components/common/KeyboardShortcutsHelp';
import { useRealtime, useRealtimeStatus } from '../services/realtime';
import type { StressLevel, EnergyLevel, LocationType, MatterStage } from '../context/LawityContext';

// Context Controls Panel
function ContextControls() {
  const {
    bio,
    location,
    matter,
    setBioState,
    setLocationState,
    setMatterState,
    setHelpOpen,
  } = useLawityContext();

  const [isDark, setIsDark] = useState(false);

  const toggleDarkMode = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle('dark');
  };

  const stressLevels: StressLevel[] = ['low', 'moderate', 'high', 'critical'];
  const energyLevels: EnergyLevel[] = ['depleted', 'low', 'normal', 'high'];
  const locationTypes: LocationType[] = ['office', 'court', 'home', 'travel', 'unknown'];
  const matterStages: MatterStage[] = ['intake', 'discovery', 'negotiation', 'litigation', 'closing', 'archived'];

  return (
    <>
      <motion.div
        className="fixed top-4 right-4 z-50 bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-neutral-200 dark:border-neutral-700 p-4 w-72"
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-neutral-500" />
            <span className="text-sm font-semibold text-neutral-900 dark:text-white">
              Context Controls
            </span>
          </div>
          <button
            onClick={toggleDarkMode}
            className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
          >
            {isDark ? (
              <Sun className="w-4 h-4 text-amber-500" />
            ) : (
              <Moon className="w-4 h-4 text-neutral-500" />
            )}
          </button>
        </div>

        {/* Bio State */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-3 h-3 text-red-500" />
            <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wider">
              Bio State
            </span>
          </div>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-neutral-500 block mb-1">Stress</label>
              <select
                value={bio.stress}
                onChange={(e) => setBioState({ stress: e.target.value as StressLevel })}
                className="w-full px-2 py-1.5 text-sm bg-neutral-50 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg"
              >
                {stressLevels.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-neutral-500 block mb-1">Energy</label>
              <select
                value={bio.energy}
                onChange={(e) => setBioState({ energy: e.target.value as EnergyLevel })}
                className="w-full px-2 py-1.5 text-sm bg-neutral-50 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg"
              >
                {energyLevels.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-3 h-3 text-blue-500" />
            <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wider">
              Location
            </span>
          </div>
          <select
            value={location.type}
            onChange={(e) => setLocationState({ type: e.target.value as LocationType })}
            className="w-full px-2 py-1.5 text-sm bg-neutral-50 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg"
          >
            {locationTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        {/* Matter */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Briefcase className="w-3 h-3 text-green-500" />
            <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wider">
              Matter
            </span>
          </div>
          <div className="space-y-2">
            <input
              type="text"
              value={matter.name || ''}
              onChange={(e) => setMatterState({ name: e.target.value })}
              placeholder="Matter name..."
              className="w-full px-2 py-1.5 text-sm bg-neutral-50 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg"
            />
            <select
              value={matter.stage || ''}
              onChange={(e) => setMatterState({ stage: (e.target.value || null) as MatterStage | null })}
              className="w-full px-2 py-1.5 text-sm bg-neutral-50 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg"
            >
              <option value="">No stage</option>
              {matterStages.map((stage) => (
                <option key={stage} value={stage}>{stage}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Keyboard Shortcuts */}
        <button
          onClick={() => setHelpOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-neutral-100 dark:bg-neutral-700 rounded-lg text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors"
        >
          <Keyboard className="w-4 h-4" />
          Keyboard Shortcuts
          <kbd className="px-1.5 py-0.5 bg-neutral-200 dark:bg-neutral-600 rounded text-xs">?</kbd>
        </button>
      </motion.div>

      <KeyboardShortcutsHelp />
    </>
  );
}

// Connection Status Indicator
function ConnectionStatus() {
  const status = useRealtimeStatus();

  const statusConfig = {
    connected: { color: 'bg-green-500', icon: Wifi, label: 'Live' },
    connecting: { color: 'bg-amber-500 animate-pulse', icon: Wifi, label: 'Connecting' },
    disconnected: { color: 'bg-neutral-400', icon: WifiOff, label: 'Offline' },
    error: { color: 'bg-red-500', icon: WifiOff, label: 'Error' },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800">
      <div className={`w-2 h-2 rounded-full ${config.color}`} />
      <Icon className="w-3 h-3 text-neutral-500" />
      <span className="text-xs text-neutral-500">{config.label}</span>
    </div>
  );
}

// Main Dashboard Content
function DashboardContent() {
  const { setCommandPaletteOpen, ui } = useLawityContext();

  // Initialize real-time connection
  useRealtime();

  // Keyboard shortcut for command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setCommandPaletteOpen]);

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-lg border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
              L
            </div>
            <div>
              <h1 className="font-semibold text-neutral-900 dark:text-white">Lawity</h1>
              <p className="text-xs text-neutral-500">Calm Law Practice Management</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Connection Status */}
            <ConnectionStatus />

            {/* Layout Indicator */}
            <div className="px-3 py-1.5 bg-neutral-100 dark:bg-neutral-800 rounded-full text-xs text-neutral-600 dark:text-neutral-400">
              Layout: <span className="font-semibold capitalize">{ui.suggestedLayout}</span>
              {ui.simplifiedMode && (
                <span className="ml-2 text-amber-600">• Simplified</span>
              )}
            </div>

            {/* Command Palette Trigger */}
            <button
              onClick={() => setCommandPaletteOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 dark:bg-neutral-800 rounded-lg text-sm text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
            >
              <span>Search...</span>
              <kbd className="px-1.5 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded text-xs">⌘K</kbd>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <MorphingDashboard />
      </main>

      {/* Context Controls */}
      <ContextControls />

      {/* Command Palette */}
      <CommandPalette />
    </div>
  );
}

// Export with Provider
export function LawityDashboard() {
  return (
    <LawityProvider
      initialBio={{
        stress: 'moderate',
        energy: 'normal',
        sleepScore: 75,
        readinessScore: 70,
      }}
      initialLocation={{
        type: 'office',
        isWorkHours: true,
      }}
      initialMatter={{
        name: 'Thompson Estate',
        stage: 'closing',
      }}
    >
      <DashboardContent />
    </LawityProvider>
  );
}

export default LawityDashboard;
